import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../context';
import type { PlexItem } from '../plex';
import { usePrefs } from '../prefs';
import { goBack } from '../router';

/**
 * Bioscoopmodus: vóór een film eerst een paar trailers van films uit je eigen bibliotheek die je nog
 * niet zag (recent toegevoegd eerst). Niet bij hervatten, niet bij samen kijken en niet bij afleveringen.
 * Alles komt van je eigen Plex-server; overslaan kan altijd.
 */

interface Trailer { url: string; title: string; year?: number }

const done = new Set<string>();

async function pickTrailers(plex: ReturnType<typeof useApp>['plex'], movie: PlexItem, n: number): Promise<Trailer[]> {
  const section = (movie as { librarySectionID?: number | string }).librarySectionID;
  if (!section) return [];
  const list = await plex.get<{ MediaContainer: { Metadata?: PlexItem[] } }>(`/library/sections/${section}/all`, {
    type: 1, unwatched: 1, sort: 'addedAt:desc', 'X-Plex-Container-Start': 0, 'X-Plex-Container-Size': 40,
  }).catch(() => null);
  // Uit de 40 nieuwste ongeziene films een willekeurige greep, zodat het niet elke keer hetzelfde is
  const pool = (list?.MediaContainer.Metadata ?? []).filter((m) => m.ratingKey !== movie.ratingKey).sort(() => Math.random() - 0.5);
  const out: Trailer[] = [];
  for (const m of pool.slice(0, 12)) {
    if (out.length >= n) break;
    const full = await plex.metadata(m.ratingKey).catch(() => null);
    const extra = full?.Extras?.Metadata?.find((e) => (e as { subtype?: string }).subtype === 'trailer');
    if (!extra) continue;
    const t = await plex.metadata(extra.ratingKey).catch(() => null);
    const part = t?.Media?.[0]?.Part?.[0];
    if (part?.key) out.push({ url: plex.directUrl(part), title: m.title, year: m.year });
  }
  return out;
}

/** Zet trailers vóór de film; daarna (of bij overslaan) de film zelf. */
export function CinemaGate({ ratingKey, params, children }: { ratingKey: string; params: URLSearchParams; children: ReactNode }) {
  const { plex } = useApp();
  const prefs = usePrefs();
  const n = prefs.cinemaTrailers ?? 0;
  const eligible = n > 0 && !params.get('together') && !Number(params.get('offset') ?? 0) && params.get('cinema') !== '0' && !done.has(ratingKey);
  const [trailers, setTrailers] = useState<Trailer[] | null>(eligible ? null : []);
  const [i, setI] = useState(0);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!eligible) return;
    let alive = true;
    const t = window.setTimeout(() => alive && setTrailers([]), 8000); // nooit lang laten wachten
    plex.metadata(ratingKey)
      .then((m) => (m.type === 'movie' ? pickTrailers(plex, m, n) : []))
      .then((list) => { if (alive) setTrailers(list); })
      .catch(() => alive && setTrailers([]))
      .finally(() => window.clearTimeout(t));
    return () => { alive = false; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingKey]);

  const finished = trailers !== null && i >= trailers.length;
  useEffect(() => { if (finished) done.add(ratingKey); }, [finished, ratingKey]);

  if (finished) return <>{children}</>;
  if (trailers === null) {
    return <div className="cinema" role="status"><span className="cinema-intro">Bioscoopmodus</span><span className="subtle">Trailers zoeken…</span><button className="btn" onClick={() => setTrailers([])}>Meteen naar de film</button></div>;
  }
  const t = trailers[i];
  return (
    <div className="cinema">
      <video ref={video} key={t.url} className="cinema-video" src={t.url} autoPlay playsInline onEnded={() => setI(i + 1)} onError={() => setI(i + 1)} />
      <div className="cinema-bar" data-focus-group>
        <span><b>Trailer {i + 1} van {trailers.length}</b> · {t.title}{t.year ? ` (${t.year})` : ''} · nog niet gezien</span>
        <span className="cinema-actions">
          <button className="btn" onClick={() => goBack()}>Stoppen</button>
          {i + 1 < trailers.length && <button className="btn" onClick={() => setI(i + 1)}>Volgende trailer</button>}
          <button className="btn btn-primary" data-autofocus onClick={() => setI(trailers.length)}>Naar de film</button>
        </span>
      </div>
    </div>
  );
}
